import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Reference to the single LocalBusiness in index.html, DO NOT create a new one
const BUSINESS_REF = { "@type": "LocalBusiness", "@id": "https://2aelectronics.com/#business", "name": "2A Electronics Service" };

// FAQ Schema for repair pages
const repairFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How long does a phone screen repair take?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most screen repairs are completed same-day, typically within 30-90 minutes depending on the device model."
      }
    },
    {
      "@type": "Question",
      "name": "Do you use original parts for repairs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We offer both OEM-grade premium parts and high-quality standard parts. As an Apple Independent Repair Provider, we have access to genuine Apple parts for iPhone repairs."
      }
    },
    {
      "@type": "Question",
      "name": "What warranty do you offer on repairs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "All repairs come with a 1-month warranty covering parts and labor. If you experience any issues related to the repair, we'll fix it at no additional cost."
      }
    },
    {
      "@type": "Question",
      "name": "Do I need an appointment for repairs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No appointment is necessary - walk-ins are welcome during business hours. However, you can also book an appointment online for your convenience."
      }
    },
    {
      "@type": "Question",
      "name": "Will I lose my data during the repair?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No, your data remains safe during most repairs. We only repair the affected hardware components. However, we always recommend backing up your device before any repair."
      }
    }
  ]
};

// ============================================================
// HREFLANG MAPPING: English <-> Spanish page pairs
// ============================================================
const HREFLANG_PAIRS = {
  '/phone-repair-chicago': '/reparacion-de-telefonos-chicago',
  '/iphone-repair-chicago': '/reparacion-de-iphone-chicago',
  '/sell-my-phone-chicago': '/vender-telefono-chicago',
  '/iphone-screen-repair': '/reparacion-de-pantalla-iphone',
  '/cash-for-phones-chicago': '/compra-de-telefonos-chicago',
  // New Spanish pairs
  '/repair/samsung': '/reparacion-de-samsung-chicago',
  '/repair/macbook': '/reparacion-de-macbook-chicago',
  '/repair/ipad': '/reparacion-de-ipad-chicago',
  '/repairs/gaming': '/reparacion-de-consolas-chicago',
  '/sell/laptops': '/vender-mi-macbook',
  '/sell/tablets': '/vender-mi-ipad',
  '/sell/samsung': '/vender-mi-samsung',
  '/phone-repair-near-me': '/reparacion-cerca-de-mi',
  '/warranty': '/garantia-de-reparacion',
  '/mail-in': '/envio-por-correo-reparacion',
};
// Build reverse map (Spanish -> English)
const HREFLANG_REVERSE = {};
Object.entries(HREFLANG_PAIRS).forEach(([en, es]) => { HREFLANG_REVERSE[es] = en; });

// ============================================================
// HREFLANG MAPPING: English <-> Polish page pairs
// ============================================================
const HREFLANG_PAIRS_PL = {
  '/phone-repair-chicago': '/naprawa-telefonow-chicago',
  '/iphone-repair-chicago': '/naprawa-iphone-chicago',
  '/repair/samsung': '/naprawa-samsung-chicago',
  '/repairs/laptops': '/naprawa-laptopow-chicago',
  '/sell-my-phone-chicago': '/sprzedaj-telefon-chicago',
  '/sell/iphone': '/sprzedaj-iphone',
  '/iphone-screen-repair': '/naprawa-ekranu-iphone',
  '/phone-repair-near-me': '/naprawa-blisko-mnie',
  '/mail-in': '/serwis-wysylkowy',
  '/warranty': '/gwarancja-naprawy',
};
const HREFLANG_REVERSE_PL = {};
Object.entries(HREFLANG_PAIRS_PL).forEach(([en, pl]) => { HREFLANG_REVERSE_PL[pl] = en; });

// Spanish page paths
const SPANISH_PATHS = new Set(Object.values(HREFLANG_PAIRS));
// Polish page paths
const POLISH_PATHS = new Set(Object.values(HREFLANG_PAIRS_PL));

// ============================================================
// PAGE TYPE DETECTION for WebPage schema
// ============================================================
const getPageType = (path) => {
  if (path === '/') return 'WebPage';
  if (path === '/about') return 'AboutPage';
  if (path === '/contact' || path === '/contact-us') return 'ContactPage';
  if (path === '/faq' || path === '/faqs') return 'FAQPage';
  if (path.startsWith('/blog/')) return 'Article';
  if (path.startsWith('/case-studies/') && path !== '/case-studies') return 'Article';
  if (path === '/blog' || path === '/case-studies') return 'CollectionPage';
  if (path.startsWith('/all-repairs') || path.startsWith('/all-sell')) return 'CollectionPage';
  if (path.startsWith('/repair-cost-guides') || path.startsWith('/compare-repair-shops') || path.startsWith('/chicago-neighborhoods') || path.startsWith('/worth-repairing-guides')) return 'CollectionPage';
  if (path.startsWith('/repair-services-chicago') || path.startsWith('/sell-devices-chicago') || path.startsWith('/servicios-en-espanol')) return 'CollectionPage';
  if (path.startsWith('/sell-phone-in/') || path.startsWith('/repair-in/') || path.startsWith('/service-areas/')) return 'WebPage';
  if (path.startsWith('/repair') || path.startsWith('/sell') || path.startsWith('/phone-repair') || path.startsWith('/iphone-repair')) return 'WebPage';
  return 'WebPage';
};

// SEO metadata for each page
const pageSEO = {
  '/what-we-do': {
    title: 'Device Care Chicago | 2A Electronics',
    description: 'Visit 2A Electronics in Chicago for friendly in-person help with phones, tablets, laptops, and electronics.',
    keywords: 'device care Chicago, local electronics store Chicago, in-person device help, Irving Park electronics, walk-in device help'
  },
  '/services': {
    title: 'Electronics Repair Services Chicago | Microsoldering, Screens, Batteries | 2A Electronics',
    description: 'Professional electronics repair services in Chicago: screens, batteries, charging ports, water damage and board-level microsoldering repair. Fast turnaround, certified technicians, 30-day warranty.',
    keywords: 'electronics repair services chicago, microsoldering repair chicago, screen replacement chicago, battery replacement chicago, charging port repair chicago, water damage repair chicago'
  },
  '/': {
    title: 'Electronics Repair Chicago | Phones, Computers & Consoles | 2A Electronics',
    description: "Chicago's top-rated electronics repair shop. We fix phones, computers, tablets & game consoles, and pay cash for used devices. Same-day service, free diagnostics, 1-month warranty.",
    keywords: 'electronics repair chicago, phone repair chicago, computer repair chicago, iphone repair chicago, electronics buyback chicago, game console repair chicago'
  },
  '/iphone-repair-chicago': {
    title: 'iPhone Repair Chicago | Screen, Battery & Same-Day Fixes | 2A Electronics',
    description: 'Professional cell phone repair in Chicago. We fix cracked screens, dead batteries, and charging ports for iPhone, Samsung, and Google Pixel.',
    keywords: 'iphone repair chicago, cell phone repair chicago, cracked screen repair chicago, samsung repair chicago, google pixel repair chicago'
  },
  '/buyback': {
    title: 'Sell Electronics Chicago | Cash Buyback for Phones, Laptops & Consoles | 2A Electronics',
    description: "Chicago's top electronics buyback. Sell your used iPhones, Samsung phones, MacBooks, tablets, and game consoles for instant cash. Highest payouts, fast appraisal.",
    keywords: 'sell electronics chicago, electronics buyback chicago, cash for iphones chicago, sell macbook chicago, sell ps5 chicago'
  },
  '/game-console-repair-chicago': {
    title: 'Game Console Repair Chicago | PS5, Xbox, Switch | 2A Electronics',
    description: 'Fast game console repair services in Chicago. Expert fixes for HDMI ports, overheating issues, disc drives, and power errors on PlayStation, Xbox, and Switch.',
    keywords: 'game console repair chicago, ps5 repair chicago, xbox repair chicago, nintendo switch repair chicago, hdmi port repair chicago'
  },
  '/repair': {
    title: 'Phone Repair Services Chicago | iPhone, Samsung, MacBook | 2A Electronics',
    description: 'Professional phone repair in Chicago. iPhone screen repair, Samsung screen repair, MacBook repair. Same-day service with 1-month warranty. Walk-in or mail-in.',
    keywords: 'phone repair, screen repair, battery replacement, iPhone repair Chicago'
  },
  '/repairs': {
    title: 'Device Repair Services | iPhone, Samsung, iPad, MacBook | 2A Electronics Chicago',
    description: 'Expert device repair for iPhones, Samsung, iPads, MacBooks, PlayStation & Xbox in Chicago. Same-day service, 1-month warranty. Walk-in or mail-in available.',
    keywords: 'device repair Chicago, phone repair, tablet repair, laptop repair, game console repair'
  },
  '/repairs/phones': {
    title: 'Phone Repair Chicago | iPhone & Samsung Screen Repair | 2A Electronics',
    description: 'Professional phone repair in Chicago for iPhone, Samsung Galaxy, Google Pixel. Screen replacement, battery, charging port repair. Same-day service.',
    keywords: 'phone repair Chicago, iPhone repair, Samsung repair, cell phone repair near me'
  },
  '/repair/iphone': {
    title: 'iPhone Repair Chicago | Screen, Battery, Charging Port | 2A Electronics',
    description: 'Expert iPhone repair in Chicago. Screen replacement, battery replacement, charging port repair for all iPhone models. Same-day service, 1-month warranty.',
    keywords: 'iPhone repair Chicago, iPhone screen repair, iPhone battery replacement'
  },
  '/repair/samsung': {
    title: 'Samsung Repair Chicago | Galaxy Screen & Battery | 2A Electronics',
    description: 'Professional Samsung Galaxy repair in Chicago. Screen replacement for S24, S23, Z Fold, Z Flip. Same-day service with warranty.',
    keywords: 'Samsung repair Chicago, Galaxy screen repair, Samsung battery replacement'
  },
  '/repair/macbook': {
    title: 'MacBook Repair Chicago | Screen, Battery, Keyboard | 2A Electronics',
    description: 'Expert MacBook repair in Chicago. Screen replacement, battery replacement, keyboard repair for all MacBook models. Fast turnaround.',
    keywords: 'MacBook repair Chicago, MacBook screen repair, MacBook battery replacement'
  },
  '/repair/ipad': {
    title: 'iPad Repair Chicago | Screen & Battery Replacement | 2A Electronics',
    description: 'Professional iPad repair in Chicago. Screen replacement, battery replacement for all iPad models. Same-day service available.',
    keywords: 'iPad repair Chicago, iPad screen repair, iPad battery replacement'
  },
  '/repairs/tablets': {
    title: 'Tablet Repair Chicago | iPad, Galaxy Tab, Surface | 2A Electronics',
    description: 'Expert tablet repair in Chicago. iPad screen repair, Samsung Galaxy Tab repair, Surface repair. Same-day service, 1-month warranty.',
    keywords: 'tablet repair Chicago, iPad repair, Samsung tablet repair, Surface repair'
  },
  '/repairs/laptops': {
    title: 'Laptop Repair Chicago | MacBook & Windows | 2A Electronics',
    description: 'Professional laptop repair in Chicago. MacBook, Dell, HP, Lenovo repairs. Screen, battery, keyboard, motherboard repair.',
    keywords: 'laptop repair Chicago, MacBook repair, computer repair, notebook repair'
  },
  '/repairs/gaming': {
    title: 'Game Console Repair Chicago | PS5 Xbox Nintendo | 2A Electronics',
    description: 'Expert PlayStation, Xbox, Nintendo Switch repair in Chicago. HDMI port, disc drive, overheating fixes. Fast turnaround.',
    keywords: 'game console repair Chicago, PS5 repair, Xbox repair, Nintendo Switch repair'
  },
  '/repair/playstation': {
    title: 'PlayStation Repair Chicago | PS5 & PS4 HDMI, Disc Drive | 2A Electronics',
    description: 'Expert PlayStation repair in Chicago. PS5 and PS4 HDMI port repair, disc drive repair, overheating fixes. Fast turnaround.',
    keywords: 'PlayStation repair Chicago, PS5 repair, PS4 HDMI repair'
  },
  '/repair/xbox': {
    title: 'Xbox Repair Chicago | Series X/S & One | 2A Electronics',
    description: 'Professional Xbox repair in Chicago. Xbox Series X, Series S, and Xbox One repairs. HDMI, disc drive, power issues.',
    keywords: 'Xbox repair Chicago, Xbox Series X repair, Xbox One repair'
  },
  '/sell': {
    title: 'Sell Your Phone, Laptop, Tablet for Cash | Instant Quote | 2A Electronics',
    description: 'Sell your iPhone, Samsung, MacBook, iPad, Apple Watch, PS5, Xbox for top dollar. Instant quotes, FREE shipping, same-day payment. We buy devices in good working condition. Competitive prices.',
    keywords: 'sell my phone, sell iPhone, sell Samsung, sell MacBook, sell laptop, sell tablet, sell iPad, trade in phone, sell phone for cash, sell device near me, phone trade in value'
  },
  '/sell/iphone': {
    title: 'Sell iPhone for Cash | Best iPhone Trade-In Prices | 2A Electronics',
    description: 'Sell your iPhone for the best price. We buy all iPhone models from iPhone 8 to iPhone 16 Pro Max in good working condition. Instant quote, free shipping, same-day cash payment.',
    keywords: 'sell iPhone, trade in iPhone, sell used iPhone, sell iPhone for cash, iPhone trade in value, sell old iPhone, best place to sell iPhone'
  },
  '/sell/samsung': {
    title: 'Sell Samsung Galaxy Phone | Best Trade-In Prices | 2A Electronics',
    description: 'Sell your Samsung Galaxy for top dollar. We buy Galaxy S25, S24, S23, Note, A series, Z Fold, Z Flip in good working condition. Instant quote, free shipping, same-day payment.',
    keywords: 'sell Samsung, sell Samsung Galaxy, trade in Samsung, sell Galaxy S24, sell Galaxy Z Fold, Samsung trade in value, sell Samsung for cash'
  },
  '/sell/phones': {
    title: 'Sell Your Phone for Cash | iPhone, Samsung, Pixel | Competitive Prices | 2A Electronics',
    description: 'Sell your phone for the best price. We buy iPhones, Samsung Galaxy, Google Pixel, OnePlus, and more in good working condition. Instant quotes, free shipping, same-day payment.',
    keywords: 'sell phone, sell my phone, sell phone for cash, sell used phone, phone trade in, sell phone near me, best place to sell phone'
  },
  '/sell/laptops': {
    title: 'Sell Your Laptop for Cash | MacBook, Dell, HP | Competitive Prices | 2A Electronics',
    description: 'Sell your laptop for top dollar. MacBook Pro, MacBook Air, Dell XPS, HP Spectre, Lenovo ThinkPad, we buy all brands in good working condition. Free shipping, same-day payment.',
    keywords: 'sell laptop, sell MacBook, sell used laptop, laptop trade in, sell MacBook Pro, sell MacBook Air, sell laptop for cash, best place to sell laptop'
  },
  '/sell/tablets': {
    title: 'Sell Tablet Chicago | iPad, Galaxy Tab, Surface | Instant Quote | 2A Electronics',
    description: 'Sell your tablet in Chicago for top cash. iPad Pro, iPad Air, iPad Mini, Samsung Galaxy Tab, Surface, good working condition. Free instant quote, same-day payment.',
    keywords: 'sell tablet chicago, sell ipad chicago, sell galaxy tab chicago, tablet buyback chicago, sell used tablet chicago'
  },
  '/sell/consoles': {
    title: 'Sell Game Console Chicago | PS5, Xbox, Switch | Instant Cash | 2A Electronics',
    description: 'Sell your game console in Chicago for top cash. PlayStation 5, Xbox Series X|S, Nintendo Switch, good working condition. Instant quote and same-day cash payment.',
    keywords: 'sell game console chicago, sell ps5 chicago, sell xbox chicago, sell nintendo switch chicago, console buyback chicago'
  },
  '/sell/watches': {
    title: 'Sell Apple Watch Chicago | All Series, Instant Cash | 2A Electronics',
    description: 'Sell your Apple Watch or smartwatch in Chicago for instant cash. Apple Watch Ultra, Series 4-10, SE, Samsung Galaxy Watch. Free quote, same-day payment.',
    keywords: 'sell apple watch chicago, sell smartwatch chicago, apple watch buyback chicago, sell used apple watch chicago'
  },
  '/contact': {
    title: 'Contact Us | 2A Electronics Service Chicago',
    description: 'Contact 2A Electronics Service in Chicago. Visit us at 4859 W Irving Park Rd, call (773) 766-5814, or email us. Open Mon-Sat.',
    keywords: 'contact 2A Electronics, phone repair near me, Chicago electronics repair'
  },
  '/about': {
    title: 'About Us | 2A Electronics Service Chicago',
    description: 'Learn about 2A Electronics Service, Chicago\'s trusted electronics repair shop. Apple Independent Repair Provider certified.',
    keywords: 'about 2A Electronics, Chicago phone repair shop, AIRP certified'
  },
  '/track-repair': {
    title: 'Track Your Repair | 2A Electronics',
    description: 'Track your repair order status at 2A Electronics. Enter your order ID to see real-time updates on your device repair.',
    keywords: 'track repair, repair status, order tracking'
  },
  '/track-sale': {
    title: 'Track Your Sale | 2A Electronics',
    description: 'Track your device sale status at 2A Electronics. See updates on your trade-in and payment.',
    keywords: 'track sale, trade in status, sell phone tracking'
  },
  '/warranty': {
    title: 'Warranty Policy | 2A Electronics',
    description: '1-month warranty on all repairs at 2A Electronics. Learn about our warranty coverage and terms.',
    keywords: 'repair warranty, phone repair warranty, 1-month warranty'
  },
  '/mobile-repair-sale': {
    title: 'Mobile Repair Service Chicago | We Come To You | 2A Electronics',
    description: 'Mobile phone repair service in Chicago. We come to your home or office for iPhone and Samsung repairs. Book online now!',
    keywords: 'mobile repair service Chicago, phone repair at home, repair near me'
  },
  '/phone-repair-near-me': {
    title: 'Phone Repair Near Me | Same Day Service | 2A Electronics Chicago',
    description: 'Looking for phone repair near you in Chicago? 2A Electronics offers same-day iPhone & Samsung repair with 1-month warranty.',
    keywords: 'phone repair near me, cell phone repair near me, iPhone repair near me'
  },
  '/iphone-repair-near-me': {
    title: 'iPhone Repair Near Me | Chicago | 2A Electronics',
    description: 'Need iPhone repair near you? 2A Electronics in Chicago offers same-day screen, battery & charging port repair.',
    keywords: 'iPhone repair near me, Apple repair near me, iPhone screen repair near me'
  },
  '/sell-phone-near-me': {
    title: 'Sell Phone Near Me | Get Cash Today | Competitive Prices in All 50 States | 2A Electronics',
    description: 'Sell your phone near you for top dollar. We buy iPhones, Samsung, tablets, laptops from all 50 states. Instant quote, free shipping, same-day cash payment. Competitive pricing.',
    keywords: 'sell phone near me, sell my phone near me, sell phone for cash near me, trade in phone near me, sell iPhone near me, sell Samsung near me, where to sell phone, best place to sell phone near me'
  },
  '/blog': {
    title: 'Blog | Electronics Repair Tips & News | 2A Electronics',
    description: 'Read the latest tips on phone repair, device maintenance, tech news and guides from 2A Electronics Chicago.',
    keywords: 'phone repair blog, tech tips, electronics repair guide, device maintenance'
  },
  '/faq': {
    title: 'FAQ | Frequently Asked Questions | 2A Electronics Chicago',
    description: 'Get answers to common questions about phone repair, sell/trade-in, shipping, warranty, and more at 2A Electronics.',
    keywords: 'phone repair FAQ, electronics repair questions, trade in FAQ'
  },
  '/faqs': {
    title: 'FAQ | Frequently Asked Questions | 2A Electronics Chicago',
    description: 'Get answers to common questions about phone repair, sell/trade-in, shipping, warranty, and more at 2A Electronics.',
    keywords: 'phone repair FAQ, electronics repair questions, trade in FAQ'
  },
  '/loyalty': {
    title: '2A Club Loyalty Program | Earn Rewards | 2A Electronics',
    description: 'Join the 2A Club loyalty program. Earn points on every repair and purchase. Get discounts, free services, and exclusive offers.',
    keywords: 'loyalty program, repair rewards, earn points, 2A Club'
  },
  '/referral': {
    title: 'Referral Program | Earn Cash | 2A Electronics',
    description: 'Refer friends to 2A Electronics and earn cash rewards. Both you and your friend get rewarded!',
    keywords: 'referral program, earn money, refer a friend, phone repair referral'
  },
  '/trade-in': {
    title: 'Trade-In Your Device | Instant Quote | 2A Electronics',
    description: 'Trade in your old phone, tablet, or laptop for cash or store credit at 2A Electronics Chicago.',
    keywords: 'trade in phone, device trade in, phone trade in value'
  },
  '/book-appointment': {
    title: 'Book Appointment | Schedule Repair | 2A Electronics Chicago',
    description: 'Book a repair appointment at 2A Electronics. Skip the wait and get your device fixed at your preferred time.',
    keywords: 'book repair appointment, schedule repair, phone repair appointment Chicago'
  },
  '/book': {
    title: 'Book Appointment | Schedule Repair | 2A Electronics Chicago',
    description: 'Book a repair appointment at 2A Electronics. Skip the wait and get your device fixed at your preferred time.',
    keywords: 'book repair appointment, schedule repair, phone repair appointment Chicago'
  },
  '/mail-in': {
    title: 'Mail-In Repair Service | Free Shipping | 2A Electronics',
    description: 'Mail your device from anywhere in the USA for expert repair. Free shipping labels, fast turnaround, 1-month warranty.',
    keywords: 'mail in phone repair, ship phone for repair, nationwide phone repair'
  },
  '/shop': {
    title: 'Shop Parts & Accessories | 2A Electronics Chicago',
    description: 'Shop phone parts, accessories, cases, chargers and more at 2A Electronics. Quality products at great prices.',
    keywords: 'phone accessories, phone parts, cases, chargers, screen protectors'
  },
  '/refurbished': {
    title: 'Certified Refurbished Phones | 2A Electronics Chicago',
    description: 'Buy certified refurbished iPhones, Samsung phones, and more. Tested, cleaned, and backed by warranty.',
    keywords: 'refurbished phones, certified pre-owned, used iPhone, used Samsung'
  },
  '/diagnostic': {
    title: 'Free Device Diagnostic | 2A Electronics Chicago',
    description: 'Get a free diagnostic for your phone, tablet, or laptop. Our AI-powered tool helps identify issues instantly.',
    keywords: 'free phone diagnostic, device troubleshoot, phone problem diagnosis'
  },
  '/price-comparison': {
    title: 'Repair Price Comparison | Competitive Prices | 2A Electronics',
    description: 'Compare our repair prices with competitors. We offer the best prices for iPhone, Samsung, and laptop repairs in Chicago.',
    keywords: 'repair price comparison, cheapest phone repair, best repair prices Chicago'
  },
  '/apple-repair-provider': {
    title: 'Apple Independent Repair Provider | 2A Electronics Chicago',
    description: '2A Electronics is an Apple Independent Repair Provider. Get genuine Apple parts and certified repairs for your iPhone, iPad, and Mac.',
    keywords: 'Apple repair provider, AIRP, genuine Apple parts, Apple Independent Repair Provider Chicago'
  },
  '/locations': {
    title: 'Our Locations | 2A Electronics Service',
    description: '2A Electronics repair shop locations. Visit us in Chicago for same-day phone, tablet, and laptop repair.',
    keywords: '2A Electronics location, phone repair Chicago address, electronics repair near me'
  },
  '/service-areas': {
    title: 'Service Areas | Nationwide Repair | 2A Electronics',
    description: '2A Electronics serves all 50 states with mail-in repair. Walk-in service in Chicago and surrounding areas.',
    keywords: 'phone repair service areas, nationwide repair, mail-in repair states'
  },
  '/privacy-policy': {
    title: 'Privacy Policy | 2A Electronics Service',
    description: 'Read the privacy policy of 2A Electronics Service. Learn how we protect your personal data.',
    keywords: 'privacy policy, data protection, 2A Electronics privacy'
  },
  '/terms-and-conditions': {
    title: 'Terms & Conditions | 2A Electronics Service',
    description: 'Read the terms and conditions of 2A Electronics Service.',
    keywords: 'terms and conditions, terms of service, 2A Electronics terms'
  },
  '/sell-iphone-near-me': {
    title: 'Sell iPhone Near Me | Competitive Prices | 2A Electronics Chicago',
    description: 'Sell your iPhone near you in Chicago. Competitive prices. Instant quote, same-day payment.',
    keywords: 'sell iPhone near me, sell used iPhone near me, trade in iPhone near me'
  },
  '/laptop-repair-near-me': {
    title: 'Laptop Repair Near Me | MacBook & Windows | 2A Electronics',
    description: 'Professional laptop repair near you in Chicago. MacBook, Dell, HP, Lenovo. Same-day service available.',
    keywords: 'laptop repair near me, MacBook repair near me, computer repair near me'
  },
  '/sell-laptop-near-me': {
    title: 'Sell Laptop Near Me | Competitive Prices | 2A Electronics',
    description: 'Sell your laptop near you for top dollar. We buy MacBooks, Dell, HP, Lenovo. Instant quote available.',
    keywords: 'sell laptop near me, sell MacBook near me, trade in laptop near me'
  },
  '/repairs/playstation': {
    title: 'PlayStation Repair Chicago | PS5 & PS4 HDMI, Overheating | 2A Electronics',
    description: 'Expert PlayStation repair in Chicago. PS5 HDMI port, disc drive, overheating repair. Fast turnaround, warranty included.',
    keywords: 'PlayStation repair Chicago, PS5 repair, PS4 repair, console repair'
  },
  '/repairs/xbox': {
    title: 'Xbox Repair Chicago | Series X/S & One | 2A Electronics',
    description: 'Professional Xbox repair in Chicago. Series X, Series S, One repairs. HDMI, disc drive, power issues.',
    keywords: 'Xbox repair Chicago, Xbox Series X repair, Xbox One repair'
  },
  '/repairs/nintendo': {
    title: 'Nintendo Repair Chicago | Switch & 3DS | 2A Electronics',
    description: 'Expert Nintendo Switch repair in Chicago. Joy-Con drift, screen, battery replacement. Fast turnaround.',
    keywords: 'Nintendo repair Chicago, Switch repair, Joy-Con drift fix'
  },
  '/repairs/watches': {
    title: 'Smartwatch Repair Chicago | Apple Watch & Samsung | 2A Electronics',
    description: 'Professional smartwatch repair in Chicago. Apple Watch screen, battery replacement. Samsung Galaxy Watch repair.',
    keywords: 'smartwatch repair Chicago, Apple Watch repair, Galaxy Watch repair'
  },
  '/repairs/computers': {
    title: 'Computer Repair Chicago | Desktop & iMac | 2A Electronics',
    description: 'Expert desktop computer repair in Chicago. iMac, PC repair. Hardware upgrades, system optimization.',
    keywords: 'computer repair Chicago, desktop repair, iMac repair, PC repair'
  },
  '/all-repairs': {
    title: 'All Repair Services | Complete Device Repair | 2A Electronics',
    description: 'Browse all repair services at 2A Electronics. iPhone, Samsung, iPad, MacBook, PlayStation, Xbox, Nintendo, Apple Watch.',
    keywords: 'all repair services, phone repair, tablet repair, laptop repair, console repair'
  },
  '/all-sell': {
    title: 'Sell Any Device for Cash | iPhone, Samsung, MacBook, iPad, PS5 | 2A Electronics',
    description: 'Complete list of devices we buy: iPhones, Samsung Galaxy, iPads, MacBooks, PS5, Xbox, Nintendo Switch, Apple Watch. Best prices, free shipping, same-day payment from all 50 states.',
    keywords: 'sell any device, sell electronics for cash, sell phone, sell laptop, sell tablet, sell game console, sell smartwatch, device trade in, electronics buyback'
  },
  '/business': {
    title: 'Business & Corporate Repair Services | 2A Electronics',
    description: 'Enterprise device repair and management for businesses. Volume discounts, priority service, fleet management.',
    keywords: 'business phone repair, corporate repair service, enterprise device management'
  },
  '/contact-us': {
    title: 'Contact Us | 2A Electronics Service Chicago',
    description: 'Contact 2A Electronics Service in Chicago. Visit us at 4859 W Irving Park Rd, call (773) 766-5814, or email us.',
    keywords: 'contact 2A Electronics, phone repair near me, Chicago electronics repair'
  },
  '/accessibility': {
    title: 'Accessibility Statement | 2A Electronics Service',
    description: 'Accessibility statement for 2A Electronics Service website.',
    keywords: 'accessibility, ADA compliance, 2A Electronics'
  },
  '/repair-iphone': {
    title: 'iPhone Repair Service | All Models | Same-Day Fix | 2A Electronics',
    description: 'Expert iPhone repair for all models. Screen replacement, battery swap, charging port, water damage. iPhone 16, 15, 14, 13, 12, 30-min repair, 1-month warranty.',
    keywords: 'iPhone repair, fix iPhone, iPhone screen repair, iPhone battery replacement, iPhone repair near me'
  },
  '/repair-samsung': {
    title: 'Samsung Galaxy Repair Service | All Models | Same-Day Fix | 2A Electronics',
    description: 'Expert Samsung Galaxy repair. Screen, battery, charging port for Galaxy S25, S24, Z Fold, Z Flip. Same-day service, 1-month warranty.',
    keywords: 'Samsung repair, Galaxy repair, Samsung screen repair, Galaxy battery replacement'
  },
  '/repair-macbook': {
    title: 'MacBook Repair Service | Pro & Air | Same-Day Fix | 2A Electronics',
    description: 'Expert MacBook repair. Screen, battery, keyboard, logic board for MacBook Pro and Air. M1, M2, M3 chips. Same-day service.',
    keywords: 'MacBook repair, MacBook screen repair, MacBook battery replacement, MacBook Pro repair'
  },
  '/repair-ipad': {
    title: 'iPad Repair Service | All Models | Same-Day Fix | 2A Electronics',
    description: 'Expert iPad repair. Screen, battery, charging port for iPad Pro, Air, Mini. All generations. Same-day service, 1-month warranty.',
    keywords: 'iPad repair, iPad screen repair, iPad battery replacement, iPad Pro repair'
  },
  '/repair-or-replace-calculator': {
    title: 'Repair or Replace Calculator | 2A Electronics Chicago',
    description: 'Not sure if it\'s worth fixing your phone? Use our free Repair or Replace Calculator to instantly find out the smartest choice for your device and budget.',
    keywords: 'repair or replace phone, phone repair calculator, should I fix my phone'
  },
  '/guide/iphone-repair-guide-2026': {
    title: 'Complete Guide to iPhone Repair in 2026 | Expert Tips | 2A Electronics',
    description: 'The ultimate guide to iPhone repair in 2026. Screen repair, battery replacement, water damage recovery. Expert advice from hundreds of repairs.',
    keywords: 'iPhone repair guide 2026, iPhone screen repair tips, complete repair guide'
  },
  '/guide/how-to-sell-phone-2026': {
    title: 'How to Get the Most Money for Your Used Phone in 2026 | Complete Guide',
    description: 'The ultimate guide to selling your used phone in 2026. When to sell, where to get the best price, maximize your payout.',
    keywords: 'sell phone guide 2026, best place to sell phone, maximize phone resale value'
  },
  '/symptom-checker': {
    title: "What's Wrong With My Phone? Free Symptom Checker | 2A Electronics",
    description: 'Free device symptom checker. Describe what\'s wrong with your iPhone, Samsung, iPad, MacBook, or game console and get an instant diagnosis with repair price estimate.',
    keywords: "what's wrong with my phone, phone symptom checker, device diagnosis, phone screen flickering, phone not charging, repair cost estimate, free diagnostic"
  },
  '/whats-wrong-with-my-phone': {
    title: "What's Wrong With My Phone? Instant Diagnosis Tool | 2A Electronics",
    description: 'Describe your phone symptoms and get an instant diagnosis. Free symptom checker for iPhone, Samsung, iPad, MacBook issues with repair price estimates.',
    keywords: "what's wrong with my phone, why is my phone screen flickering, phone won't charge, phone diagnosis tool"
  },
  '/device-diagnosis': {
    title: 'Free Device Diagnosis Online | Instant Results | 2A Electronics',
    description: 'Get a free online diagnosis for your broken device. Identify the problem and see repair costs instantly for iPhone, Samsung, iPad, MacBook, PS5, Xbox.',
    keywords: 'device diagnosis, online phone diagnostic, free device check, repair estimate tool'
  },
  '/servicios-en-espanol': {
    title: 'Reparacion de Telefonos Chicago | Hablamos Espanol | 2A Electronics',
    description: 'Servicios de reparacion de telefonos y compra de dispositivos en Chicago. Hablamos espanol. iPhone, Samsung, laptops. Servicio el mismo dia.',
    keywords: 'reparacion telefonos chicago, hablamos espanol, reparacion iphone espanol, vender telefono espanol'
  },
  '/case-studies': {
    title: 'Real Repair Case Studies | Before & After | 2A Electronics Chicago',
    description: 'See real repair case studies from 2A Electronics Chicago. Before and after details, repair costs, and customer testimonials for iPhone, Samsung, MacBook, PS5, iPad repairs.',
    keywords: 'repair case studies, phone repair before after, iPhone repair case study, electronics repair examples, real repair results Chicago'
  },
  '/uslugi-po-polsku': {
    title: 'Naprawa Telefonow Chicago | Mowimy po Polsku | 2A Electronics',
    description: 'Uslugi naprawy telefonow i skupu urzadzen w Chicago. Mowimy po polsku. iPhone, Samsung, laptopy. Serwis tego samego dnia.',
    keywords: 'naprawa telefonow chicago polski, mowimy po polsku, naprawa iphone chicago polski, sprzedaj telefon chicago polski'
  },

  // ========= SEO GROWTH PACK 2, Unique titles/descriptions per page =========
  // (Prevents the "default SEO fallback" duplicate-content issue.)
  '/iphone-hub': {
    title: 'iPhone Repair & Buyback Complete Guide 2026 | Pricing, Models, FAQ | 2A Electronics Chicago',
    description: 'Everything about iPhone repair and buyback in Chicago: model-by-model pricing, common issues, expert FAQ, and trade-in values. hundreds of repairs. Apple Independent Repair Provider.',
    keywords: 'iPhone repair Chicago guide, iPhone repair pricing 2026, iPhone trade-in value, Apple Independent Repair Provider, iPhone repair cost'
  },
  '/samsung-hub': {
    title: 'Samsung Galaxy Repair & Buyback Complete Guide 2026 | S25, Z Fold, Z Flip | 2A Electronics',
    description: 'Complete Samsung Galaxy repair pricing and trade-in guide for Chicago. S25 Ultra, Z Fold, Z Flip foldables, Note series. Same-day service, 1-month warranty.',
    keywords: 'Samsung repair Chicago guide, Galaxy S25 repair, Z Fold repair, Z Flip repair, Samsung trade-in value'
  },
  '/macbook-hub': {
    title: 'MacBook Repair & Buyback Complete Guide 2026 | Pro, Air, Logic Board | 2A Electronics',
    description: 'MacBook Pro and Air repair guide for Chicago: screen, battery, keyboard, microsoldering, logic-board repair. Apple Independent Repair Provider with component-level expertise.',
    keywords: 'MacBook repair Chicago guide, MacBook Pro repair, MacBook Air battery, MacBook logic board repair, microsoldering'
  },
  '/free-repair-resources': {
    title: 'Free Repair Resources | Phone & Device Prep Checklists | 2A Electronics Chicago',
    description: 'Free repair, diagnostic, and device buyback checklists from 2A Electronics Chicago. Pre-repair prep, water damage first response, data backup & wipe, screen vs LCD diagnosis, and sell prep. Print or read free.',
    keywords: 'free repair checklist, phone repair prep, water damage steps, factory reset before selling, screen vs LCD damage, device buyback checklist Chicago'
  },
  '/tools': {
    title: 'Free Phone & Device Tools | IMEI Check, Repair Calculator, Trade-In Estimator | 2A Electronics',    description: 'Free calculators and tools for phone owners: IMEI blacklist checker, repair cost calculator, trade-in estimator, water damage quiz. No signup required.',
    keywords: 'free phone tools, IMEI checker, repair cost calculator, trade-in estimator, water damage diagnostic'
  },
  '/tools/imei-checker': {
    title: 'Free IMEI Checker | Validate, Identify, Find Blacklist Status | 2A Electronics',
    description: 'Free IMEI checker for any phone. Validate the 15-digit number, identify the device model, and learn how to check carrier blacklist status before buying or selling.',
    keywords: 'free IMEI checker, IMEI lookup, validate IMEI, blacklisted phone check, IMEI carrier check'
  },
  '/tools/repair-cost-calculator': {
    title: 'Free Repair Cost Calculator 2026 | iPhone, Samsung, MacBook | 2A Electronics Chicago',
    description: 'Instant 2026 repair cost estimate for iPhone, Samsung, iPad, MacBook, and gaming consoles. Free, no signup. Used by 50,000+ phone owners.',
    keywords: 'repair cost calculator, iPhone repair price, MacBook repair cost, Samsung repair estimate, free repair quote'
  },
  '/tools/trade-in-estimator': {
    title: 'Free Trade-In Estimator | How Much Is My Phone Worth in 2026? | 2A Electronics',
    description: 'Find out exactly what your iPhone, Samsung, MacBook, or iPad is worth in 2026. Transparent payouts, beats carrier trade-in programs by a competitive amount.',
    keywords: 'trade-in estimator, phone trade in value, iPhone trade in 2026, sell phone calculator, device buyback estimate'
  },
  '/tools/water-damage-quiz': {
    title: 'Phone Water Damage Quiz | 30-Second Emergency Diagnostic | 2A Electronics',
    description: 'Phone dropped in water? Take our 30-second quiz to learn if your phone can be saved and what to do RIGHT NOW. Free emergency diagnostic.',
    keywords: 'water damage phone quiz, phone dropped in water, water damaged iPhone, emergency phone repair, save wet phone'
  },
  '/team': {
    title: 'Meet Our Repair Team | Certified Technicians | 2A Electronics Chicago',
    description: 'Meet the certified technicians at 2A Electronics Chicago. Combined hands-on experience, hundreds of devices repaired.',
    keywords: 'phone repair technicians Chicago, Apple Independent Repair Provider team, 2A Electronics staff, repair technician bio'
  },
  '/qa': {
    title: 'Expert Q&A Library | Phone Repair Questions Answered | 2A Electronics Chicago',
    description: 'Get expert answers about phone repair, trade-in values, water damage, foldable repair, and more, from certified Chicago repair technicians.',
    keywords: 'phone repair questions, repair FAQ Chicago, expert phone repair Q&A, electronics repair questions answered'
  },
  '/repair-gallery': {
    title: 'Before & After Repair Gallery | Real Repairs at 2A Electronics Chicago',
    description: 'See real before-and-after photos from hundreds of device repairs at 2A Electronics in Chicago. iPhone, Samsung, MacBook, PS5, iPad repairs with prices.',
    keywords: 'repair before after photos, iPhone repair gallery, real phone repair results Chicago, MacBook water damage repair, repair examples'
  },
  '/press-kit': {
    title: 'Press & Media Kit | 2A Electronics Service | Chicago Electronics Repair',
    description: 'Press kit and media resources for journalists covering Chicago electronics repair, tech sustainability, and small-business stories. Logos, quotes, key facts.',
    keywords: 'press kit 2A Electronics, media inquiries Chicago repair, Chicago tech business story, electronics repair newsroom'
  }
};

// Default SEO for pages not in the list
const defaultSEO = {
  title: '2A Electronics Service | Phone Repair & Trade-In Chicago',
  description: 'Expert electronics repair and device trade-in services in Chicago. Same-day service, 1-month warranty. Nationwide mail-in available.',
  keywords: 'phone repair, electronics repair, sell phone, Chicago'
};

// Module-level cache of SEO Agent overrides (fetched once per session)
let _seoOverridesCache = null;
async function _fetchSeoOverrides() {
  if (_seoOverridesCache) return _seoOverridesCache;
  try {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${base}/api/seo/page-overrides`);
    const data = await res.json();
    _seoOverridesCache = data.overrides || {};
  } catch (e) {
    _seoOverridesCache = {};
  }
  return _seoOverridesCache;
}

const SEOHead = () => {
  const location = useLocation();
  const [overrides, setOverrides] = useState(_seoOverridesCache || {});

  useEffect(() => {
    let alive = true;
    _fetchSeoOverrides().then((ov) => { if (alive) setOverrides(ov); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    // Normalize trailing slash (ingress 301-redirects add one) so exact pageSEO lookups match
    const path = location.pathname.replace(/\/+$/, '') || '/';
    
    // Try exact match first, then prefix match for dynamic routes
    let seo = pageSEO[path];
    if (!seo) {
      // Dynamic route matching
      if (path.startsWith('/sell-in/')) {
        const city = path.replace('/sell-in/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `Sell Your Device in ${city} | Top Dollar | Free Shipping | 2A Electronics`, description: `Sell your iPhone, Samsung, MacBook, iPad in ${city} for the best price. Instant quotes, free shipping, same-day payment. We buy all devices from ${city}.`, keywords: `sell phone ${city}, sell iPhone ${city}, sell device ${city}, trade in phone ${city}, sell laptop ${city}` };
      } else if (path.startsWith('/sell-my/')) {
        const parts = path.split('/');
        const brand = (parts[2] || '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const model = (parts[3] || '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `Sell ${model || brand} for Cash | Competitive Prices | 2A Electronics`, description: `Sell your ${model || brand} for top dollar. Instant quote, FREE shipping, same-day payment. We buy ${model || brand} in good working condition. competitive pricing.`, keywords: `sell ${model || brand}, sell my ${model || brand}, ${model || brand} trade in value, sell used ${model || brand}, best place to sell ${model || brand}` };
      } else if (path.startsWith('/sell-comparison/')) {
        seo = { title: `Trade-In Price Comparison | Who Pays More? | 2A Electronics`, description: `Compare selling your device at 2A Electronics vs major competitors. See real price comparisons and find out who pays more.`, keywords: `trade in comparison, sell phone comparison, best trade in prices, who pays more for phones` };
      } else if (path.startsWith('/how-much-is-my-worth/')) {
        const device = path.replace('/how-much-is-my-worth/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `How Much Is My ${device} Worth? | 2026 Value Guide | 2A Electronics`, description: `Find out how much your ${device} is worth in 2026. Trade-in value chart by storage and condition. Get an instant cash quote.`, keywords: `${device} value, how much is ${device} worth, ${device} trade in value, ${device} worth 2026` };
      } else if (path.startsWith('/sell-guide/')) {
        seo = { title: `Device Selling Guide | Maximize Your Payout | 2A Electronics`, description: `Expert guide to selling your device for maximum value. Timing tips, depreciation insights, and pro strategies.`, keywords: `best time to sell phone, sell phone guide, maximize trade in value, phone depreciation` };
      } else if (path === '/sustainability' || path === '/recycle-electronics') {
        seo = { title: `Sell Your Device, Save the Planet | E-Waste Recycling | 2A Electronics`, description: `Sell your old phone, laptop, or tablet instead of throwing it away. Reduce e-waste, earn cash, and help the environment.`, keywords: `recycle electronics, e-waste recycling, sustainable electronics, sell old phone environment, electronics recycling` };
      } else if (path.startsWith('/seller-reviews/')) {
        const category = path.replace('/seller-reviews/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `${category} Reviews | Real Customer Stories | 2A Electronics`, description: `Read real reviews from customers who sold their devices to 2A Electronics. See actual payouts and why they chose us.`, keywords: `sell device reviews, 2A Electronics reviews, trade in reviews, selling experience, customer testimonials` };
      } else if (path === '/sell-iphone' || path === '/sell-samsung' || path === '/sell-macbook' || path === '/sell-ipad') {
        const brand = path.replace('/sell-', '').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `Sell Your ${brand} for Cash | All Models | Competitive Prices | 2A Electronics`, description: `Sell any ${brand} model for top dollar. Instant quote, free shipping, same-day cash payment.`, keywords: `sell ${brand}, sell my ${brand}, ${brand} trade in` };
      } else if (path.startsWith('/sell-storage/')) {
        seo = { title: `Sell Device by Storage Size | 2A Electronics`, description: `Sell your device based on storage capacity. Higher storage = higher payout. Instant quote.`, keywords: `sell phone by storage, storage trade in value` };
      } else if (path.startsWith('/sell-carrier/')) {
        seo = { title: `Sell Carrier Phone for Cash | 2A Electronics`, description: `Sell your carrier phone for top dollar. AT&T, T-Mobile, Verizon, and unlocked accepted.`, keywords: `sell carrier phone, sell locked phone, carrier trade in` };
      } else if (path === '/sell-or-keep-calculator') {
        seo = { title: `Should I Sell My Phone Now or Wait? | Value Calculator | 2A Electronics`, description: `Use our depreciation calculator to see how much value your device loses each month. Sell now or wait?`, keywords: `sell or keep phone, phone depreciation calculator, should I sell my phone` };
      } else if (path.startsWith('/sell-phone-in/')) {
        const state = path.replace('/sell-phone-in/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `Sell Phone in ${state} for Cash | Free Shipping | 2A Electronics`, description: `Sell your phone, laptop, or tablet in ${state}. Free shipping, instant quotes, same-day cash payment.`, keywords: `sell phone in ${state}, sell device in ${state}, ${state} phone trade in` };
      } else if (path.startsWith('/sell-year/')) {
        seo = { title: `Sell Devices by Year | Competitive Prices | 2A Electronics`, description: `Sell your devices by purchase year. All years and models accepted. Instant quote, free shipping.`, keywords: `sell old phone by year, sell 2023 phone, sell 2024 phone` };
      } else if (path === '/sell-multiple-devices') {
        seo = { title: `Sell Multiple Devices | Bundle Bonus | 2A Electronics`, description: `Sell multiple devices together and earn extra cash. Bundle bonus up to $100. Free shipping.`, keywords: `sell multiple devices, bundle sell, family device buyback, sell phone and laptop` };
      } else if (path.startsWith('/sell/') && path.split('/').length >= 3) {
        const parts = path.split('/');
        const category = parts[2] || '';
        const brand = (parts[3] || '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const model = (parts[4] || '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (model) {
          seo = { title: `Sell ${model} for Cash | Best Price | Free Shipping | 2A Electronics`, description: `Sell your ${model} for the best price. Instant quote, free shipping, same-day payment. We buy ${model} in good working condition.`, keywords: `sell ${model}, trade in ${model}, sell my ${model}, ${model} trade in value, sell used ${model}` };
        } else if (brand) {
          seo = { title: `Sell ${brand} ${category.charAt(0).toUpperCase() + category.slice(1)} | Competitive Prices | Free Shipping | 2A Electronics`, description: `Sell your ${brand} ${category} for top dollar. Instant quotes, free shipping, same-day payment. Good working condition.`, keywords: `sell ${brand}, trade in ${brand}, sell ${brand} for cash, sell used ${brand}` };
        } else {
          seo = pageSEO[`/sell/${category}`] || { title: `Sell ${category.charAt(0).toUpperCase() + category.slice(1)} for Cash | Competitive Prices | 2A Electronics`, description: `Sell your ${category} for top dollar. Instant quotes, free shipping, same-day payment. Good working condition.`, keywords: `sell ${category}, trade in ${category}, sell ${category} for cash` };
        }
      } else if (path.startsWith('/repairs/phones/')) {
        const brand = path.split('/')[3];
        seo = { title: `${brand?.charAt(0).toUpperCase()}${brand?.slice(1)} Phone Repair Chicago | 2A Electronics`, description: `Professional ${brand} phone repair in Chicago. Screen, battery, charging port repair. Same-day service, 1-month warranty.`, keywords: `${brand} repair Chicago, ${brand} screen repair, ${brand} phone repair` };
      } else if (path.startsWith('/iphone-repair/')) {
        const model = path.replace('/iphone-repair/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `${model} Repair Chicago | Screen, Battery, Charging | 2A Electronics`, description: `Expert ${model} repair in Chicago. Screen replacement, battery replacement, charging port repair. Same-day service, 1-month warranty.`, keywords: `${model} repair Chicago, ${model} screen repair` };
      } else if (path.startsWith('/samsung-repair/')) {
        const model = path.replace('/samsung-repair/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `${model} Repair Chicago | Screen, Battery | 2A Electronics`, description: `Professional ${model} repair in Chicago. Same-day service with warranty.`, keywords: `${model} repair Chicago, ${model} screen repair` };
      } else if (path.startsWith('/blog/')) {
        const slug = path.replace('/blog/', '');
        const blogTitle = slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `${blogTitle} | 2A Electronics`, description: `Read "${blogTitle}" - expert tips and guides from 2A Electronics Chicago.`, keywords: 'phone repair tips, electronics blog, repair guide' };
      } else if (path.startsWith('/service-areas/')) {
        const parts = path.replace('/service-areas/', '').split('/');
        const state = (parts[0] || '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const city = (parts[1] || '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (city) {
          seo = { title: `Phone Repair ${city}, ${state} | Mail-In Repair | 2A Electronics`, description: `Professional phone repair service in ${city}, ${state}. Mail-in your device for expert repair with free shipping and 1-month warranty.`, keywords: `phone repair ${city}, ${state} phone repair, mail in repair ${city}` };
        } else if (state) {
          seo = { title: `Phone Repair ${state} | Mail-In Repair | 2A Electronics`, description: `Phone repair service in ${state}. Mail-in your device from anywhere for expert repair with free shipping and 1-month warranty.`, keywords: `phone repair ${state}, mail in repair ${state}` };
        } else {
          seo = { title: 'Service Areas | Nationwide Repair | 2A Electronics', description: '2A Electronics serves all 50 states with mail-in repair. Walk-in service in Chicago and surrounding areas.', keywords: 'phone repair service areas, nationwide repair' };
        }
      } else if (path.startsWith('/local/')) {
        seo = { title: 'Local Phone Repair Service | 2A Electronics', description: 'Local electronics repair services by 2A Electronics. Same-day iPhone, Samsung, MacBook repair.', keywords: 'local phone repair, electronics repair near me' };
      } else if (path.startsWith('/repair-comparison/')) {
        const slug = path.replace('/repair-comparison/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `Repair Price Comparison: 2A Electronics ${slug} | 2A Electronics`, description: `Compare repair prices at 2A Electronics ${slug}. See real price comparisons and save on repairs.`, keywords: `repair comparison, ${slug.toLowerCase()}, repair price comparison` };
      } else if (path.startsWith('/repair-cost/')) {
        const slug = path.replace('/repair-cost/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `${slug} | 2026 Price Guide | 2A Electronics`, description: `How much does ${slug.toLowerCase()} cost in 2026? Complete price guide with model-by-model breakdown.`, keywords: `${slug.toLowerCase()}, repair cost guide, repair price 2026` };
      } else if (path.startsWith('/repair-in/')) {
        const state = path.replace('/repair-in/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `Device Repair in ${state} | Free Mail-In Shipping | 2A Electronics`, description: `Expert phone, laptop, and tablet repair for ${state} residents. Free mail-in shipping. Same-day turnaround, 1-month warranty.`, keywords: `phone repair ${state}, device repair ${state}, mail in repair ${state}` };
      } else if (path.startsWith('/repair-reviews/')) {
        const cat = path.replace('/repair-reviews/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `${cat} | Real Customer Stories | 2A Electronics`, description: `Read real reviews from repair customers. 5.0/5 Google rating. See why thousands choose 2A Electronics.`, keywords: `${cat.toLowerCase()}, repair reviews, 2A Electronics reviews` };
      } else if (path.startsWith('/is-it-worth-repairing-')) {
        const device = path.replace('/is-it-worth-repairing-', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `Is It Worth Repairing My ${device}? | 2026 Guide | 2A Electronics`, description: `Find out if repairing your ${device} is worth it in 2026. Compare repair cost vs replacement cost. Expert analysis and recommendations.`, keywords: `is it worth repairing ${device}, ${device} repair vs replace, should I fix my ${device}` };
      } else if (path === '/worth-repairing-guides') {
        seo = { title: 'Is It Worth Repairing? Device Guides | 2A Electronics', description: 'Find out if repairing your device is worth it. Expert guides for iPhone, MacBook, iPad, Samsung, PS5, and Nintendo Switch.', keywords: 'worth repairing guide, repair vs replace, should I fix my phone' };
      } else if (path.startsWith('/case-studies/') && path !== '/case-studies') {
        const csSlug = path.replace('/case-studies/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = { title: `${csSlug} | Repair Case Study | 2A Electronics`, description: `Read the full repair case study: ${csSlug}. See the problem, our solution, cost, and repair time.`, keywords: `repair case study, ${csSlug.toLowerCase()}, electronics repair example` };
      }
      // ========= SEO GROWTH PACK 2, Dynamic-route SEO for unique titles per URL =========
      else if (path.startsWith('/fix/')) {
        const parts = path.split('/');
        const device = (parts[2] || '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const symptomSlug = parts[3] || '';
        const symptomMap = {
          'wont-charge': "Won't Charge",
          'black-screen': 'Black Screen',
          'cracked-screen': 'Cracked Screen',
          'water-damage': 'Water Damage',
          'wont-turn-on': "Won't Turn On",
          'battery-drains-fast': 'Battery Drains Fast',
          'speaker-not-working': 'Speaker Not Working',
          'camera-not-working': 'Camera Not Working',
        };
        const symptom = symptomMap[symptomSlug] || symptomSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        seo = {
          title: `${device} ${symptom}, Fix It Same-Day in Chicago | 2A Electronics`,
          description: `Is your ${device} ${symptom.toLowerCase()}? We fix this every day at 2A Electronics Chicago. Same-day repair, 1-month warranty. Free diagnostic. Call (773) 766-5814.`,
          keywords: `${device} ${symptom.toLowerCase()}, ${device} repair Chicago, fix ${device.toLowerCase()} ${symptomSlug.replace(/-/g, ' ')}, ${device.toLowerCase()} ${symptom.toLowerCase()} fix`,
        };
      } else if (path.startsWith('/compare/')) {
        const slug = path.replace('/compare/', '');
        const compMap = {
          '2a-vs-apple-store': { c: 'Apple Store', t: 'Apple Store vs 2A Electronics: Repair Pricing & Service Compared (2026)', d: 'Honest side-by-side comparison: 2A Electronics vs Apple Store. iPhone repair pricing, warranty, walk-in availability, turnaround time. Save 30-50% on the same parts.' },
          '2a-vs-ubreakifix': { c: 'uBreakiFix', t: 'uBreakiFix vs 2A Electronics: Chicago Phone Repair Comparison (2026)', d: 'Detailed comparison of 2A Electronics vs uBreakiFix in Chicago: iPhone, Samsung, MacBook repair pricing, microsoldering capabilities, and local service.' },
          '2a-vs-icracked': { c: 'iCracked', t: 'iCracked vs 2A Electronics: Phone Repair Comparison Chicago', d: 'iCracked vs 2A Electronics: which is cheaper, faster, and offers more service options in Chicago? Side-by-side comparison.' },
          '2a-vs-verizon-trade-in': { c: 'Verizon Trade-In', t: 'Verizon Trade-In vs 2A Electronics: Who Pays More for Your Phone? (2026)', d: 'See real payout differences: Verizon Trade-In vs 2A Electronics for iPhone, Samsung, and MacBook. Cash same-day with no contracts.' },
        };
        const m = compMap[slug];
        if (m) {
          seo = { title: m.t, description: m.d, keywords: `2A Electronics vs ${m.c}, ${m.c} alternative, ${m.c} comparison, ${m.c} price` };
        }
      } else if (path.startsWith('/qa/')) {
        const slug = path.replace('/qa/', '');
        const q = slug.replace(/-/g, ' ').replace(/^\w/, l => l.toUpperCase());
        seo = {
          title: `${q}? | Expert Answer | 2A Electronics Chicago`,
          description: `Expert answer to "${q}?" from certified 2A Electronics repair technicians in Chicago. Written by professionals with hundreds of repairs.`,
          keywords: `${q}, phone repair question, expert repair answer Chicago`,
        };
      } else if (path.startsWith('/team/') && path !== '/team') {
        const slug = path.replace('/team/', '');
        const nameMap = {
          'ardit-ago': { name: 'Ardit Ago', role: 'Owner & Lead Technician', expertise: 'iPhone microsoldering, MacBook logic boards, board-level repair' },
        };
        const p = nameMap[slug];
        if (p) {
          seo = {
            title: `${p.name}, ${p.role} | 2A Electronics Chicago Team`,
            description: `${p.name}, ${p.role} at 2A Electronics Chicago. Expertise: ${p.expertise}. Apple Independent Repair Provider technician.`,
            keywords: `${p.name}, 2A Electronics team, ${p.expertise}, Chicago repair technician`,
          };
        }
      }
    }
    if (!seo) seo = defaultSEO;

    // SEO Agent override layer (approved title/meta for curated money pages)
    const ov = overrides && overrides[path];
    if (ov) {
      seo = { ...seo, title: ov.title || seo.title, description: ov.description || seo.description };
    }

    // Update document title
    document.title = seo.title;
    
    // Update meta description (skip if Helmet-managed to avoid duplicates)
    let metaDescription = document.querySelector('meta[name="description"]');
    const helmetDescription = document.querySelector('meta[name="description"][data-react-helmet="true"]');
    if (metaDescription && !helmetDescription) {
      metaDescription.setAttribute('content', seo.description);
    } else if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('content', seo.description);
      document.head.appendChild(metaDescription);
    }
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', seo.keywords);
    }
    
    // ============================================================
    // ROBOTS: max-image-preview:large, max-snippet:-1 globally
    // ============================================================
    let robotsMeta = document.querySelector('meta[name="robots"][content*="max-image-preview"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      robotsMeta.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
      document.head.appendChild(robotsMeta);
    }
    
    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', seo.title);
    }
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', seo.description);
    }
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `https://2aelectronics.com${path}`);
    }
    
    // Update Twitter tags
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', seo.title);
    }
    
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', seo.description);
    }
    
    // Update canonical URL - always create if missing
    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalUrl = `https://2aelectronics.com${path === '/' ? '' : path}`;
    if (canonical) {
      canonical.setAttribute('href', canonicalUrl);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', canonicalUrl);
      document.head.appendChild(canonical);
    }
    
    // ============================================================
    // HREFLANG TAGS for English <-> Spanish <-> Polish page pairs
    // ============================================================
    // Remove old hreflang tags
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    
    const isSpanishPage = SPANISH_PATHS.has(path);
    const isPolishPage = POLISH_PATHS.has(path);
    
    // Determine English path
    let englishPath = path;
    if (isSpanishPage) englishPath = HREFLANG_REVERSE[path];
    else if (isPolishPage) englishPath = HREFLANG_REVERSE_PL[path];
    
    // Feb 2026: Spanish/Polish landing pages are being phased out, we no
    // longer advertise multi-language support. Force-null the alternate
    // hreflang locales so Google stops indexing the legacy variants.
    const spanishPath = null;
    const polishPath = null;
    const hasAnyVariant = isSpanishPage || isPolishPage;
    
    if (hasAnyVariant) {
      // Add en hreflang
      if (englishPath) {
        const enLink = document.createElement('link');
        enLink.setAttribute('rel', 'alternate');
        enLink.setAttribute('hreflang', 'en');
        enLink.setAttribute('href', `https://2aelectronics.com${englishPath}`);
        document.head.appendChild(enLink);
      }
      // Add es hreflang if Spanish variant exists
      if (spanishPath) {
        const esLink = document.createElement('link');
        esLink.setAttribute('rel', 'alternate');
        esLink.setAttribute('hreflang', 'es');
        esLink.setAttribute('href', `https://2aelectronics.com${spanishPath}`);
        document.head.appendChild(esLink);
      }
      // Add pl hreflang if Polish variant exists
      if (polishPath) {
        const plLink = document.createElement('link');
        plLink.setAttribute('rel', 'alternate');
        plLink.setAttribute('hreflang', 'pl');
        plLink.setAttribute('href', `https://2aelectronics.com${polishPath}`);
        document.head.appendChild(plLink);
      }
      // x-default points to English version
      if (englishPath) {
        const defaultLink = document.createElement('link');
        defaultLink.setAttribute('rel', 'alternate');
        defaultLink.setAttribute('hreflang', 'x-default');
        defaultLink.setAttribute('href', `https://2aelectronics.com${englishPath}`);
        document.head.appendChild(defaultLink);
      }
    } else {
      // No language variant, just add en-us + x-default pointing to self
      const selfUrl = `https://2aelectronics.com${path === '/' ? '' : path}`;
      const enUsLink = document.createElement('link');
      enUsLink.setAttribute('rel', 'alternate');
      enUsLink.setAttribute('hreflang', 'en-us');
      enUsLink.setAttribute('href', selfUrl);
      document.head.appendChild(enUsLink);
      
      const defaultLink = document.createElement('link');
      defaultLink.setAttribute('rel', 'alternate');
      defaultLink.setAttribute('hreflang', 'x-default');
      defaultLink.setAttribute('href', selfUrl);
      document.head.appendChild(defaultLink);
    }
    
    // ============================================================
    // WEBPAGE SCHEMA (per page type)
    // ============================================================
    let webPageScript = document.getElementById('webpage-schema');
    const pageType = getPageType(path);
    if (pageType !== 'Article') { // Blog posts handle their own Article schema
      const webPageData = {
        "@context": "https://schema.org",
        "@type": pageType,
        "name": seo.title,
        "description": seo.description,
        "url": `https://2aelectronics.com${path === '/' ? '' : path}`,
        "isPartOf": { "@type": "WebSite", "@id": "https://2aelectronics.com/#website" },
        "about": { "@type": "LocalBusiness", "@id": "https://2aelectronics.com/#business" },
        "inLanguage": isSpanishPage ? "es" : isPolishPage ? "pl" : "en-US"
      };
      if (!webPageScript) {
        webPageScript = document.createElement('script');
        webPageScript.id = 'webpage-schema';
        webPageScript.type = 'application/ld+json';
        document.head.appendChild(webPageScript);
      }
      webPageScript.textContent = JSON.stringify(webPageData);
    } else if (webPageScript) {
      webPageScript.remove();
    }
    
    // ============================================================
    // SPEAKABLE SCHEMA for voice search (key pages)
    // ============================================================
    let speakableScript = document.getElementById('speakable-schema');
    const speakablePages = ['/', '/repairs', '/sell', '/phone-repair-near-me', '/sell-phone-near-me',
      '/iphone-repair-near-me', '/mail-in', '/phone-repair-chicago', '/iphone-repair-chicago',
      '/sell-my-phone-chicago', '/repair-iphone', '/repair-samsung', '/sell-iphone', '/sell-samsung',
      '/faq', '/faqs', '/contact', '/contact-us'];
    
    if (speakablePages.includes(path) || path.startsWith('/phone-repair-') || path.startsWith('/is-it-worth-repairing-')) {
      const speakableData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": seo.title,
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", ".speakable-intro", "[data-speakable]"]
        },
        "url": `https://2aelectronics.com${path === '/' ? '' : path}`
      };
      if (!speakableScript) {
        speakableScript = document.createElement('script');
        speakableScript.id = 'speakable-schema';
        speakableScript.type = 'application/ld+json';
        document.head.appendChild(speakableScript);
      }
      speakableScript.textContent = JSON.stringify(speakableData);
    } else if (speakableScript) {
      speakableScript.remove();
    }
    
    // Add/Update FAQ Schema for repair pages (LocalBusiness already in index.html)
    const isRepairPage = path.startsWith('/repair') || path.startsWith('/repairs');
    let faqScript = document.getElementById('faq-schema');
    if (isRepairPage) {
      if (!faqScript) {
        faqScript = document.createElement('script');
        faqScript.id = 'faq-schema';
        faqScript.type = 'application/ld+json';
        document.head.appendChild(faqScript);
      }
      faqScript.textContent = JSON.stringify(repairFAQSchema);
    } else if (faqScript) {
      faqScript.remove();
    }
    
    // Add/Update Breadcrumb Schema for sub-pages
    let breadcrumbScript = document.getElementById('breadcrumb-schema');
    if (path !== '/') {
      const parts = path.replace(/^\//, '').split('/');
      const labels = {
        repairs: 'Repair Services', sell: 'Sell Your Device', blog: 'Blog',
        'service-areas': 'Service Areas', phones: 'Phones', laptops: 'Laptops',
        tablets: 'Tablets', watches: 'Watches', consoles: 'Consoles',
        contact: 'Contact', about: 'About', faq: 'FAQ', 'mail-in': 'Mail-In Repair'
      };
      const items = [{ "@type": "ListItem", position: 1, name: "Home", item: "https://2aelectronics.com" }];
      let currentPath = '';
      parts.forEach((part, i) => {
        currentPath += `/${part}`;
        items.push({
          "@type": "ListItem",
          position: i + 2,
          name: labels[part] || part.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          item: `https://2aelectronics.com${currentPath}`
        });
      });
      
      const breadcrumbData = { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items };
      if (!breadcrumbScript) {
        breadcrumbScript = document.createElement('script');
        breadcrumbScript.id = 'breadcrumb-schema';
        breadcrumbScript.type = 'application/ld+json';
        document.head.appendChild(breadcrumbScript);
      }
      breadcrumbScript.textContent = JSON.stringify(breadcrumbData);
    } else if (breadcrumbScript) {
      breadcrumbScript.remove();
    }
    
    // Add Service/Product Schema for repair and sell pages
    let serviceScript = document.getElementById('service-schema');
    if (isRepairPage) {
      const repairType = path.includes('iphone') ? 'iPhone' : path.includes('samsung') ? 'Samsung' : path.includes('macbook') ? 'MacBook' : path.includes('ipad') ? 'iPad' : path.includes('playstation') || path.includes('ps5') ? 'PlayStation' : path.includes('xbox') ? 'Xbox' : path.includes('switch') || path.includes('nintendo') ? 'Nintendo Switch' : 'Device';
      const priceRanges = { 'iPhone': '$69 - $329', 'Samsung': '$79 - $499', 'MacBook': '$129 - $599', 'iPad': '$79 - $449', 'PlayStation': '$49 - $179', 'Xbox': '$49 - $179', 'Nintendo Switch': '$39 - $129', 'Device': '$29 - $599' };
      const serviceData = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `${repairType} Repair Service`,
        provider: BUSINESS_REF,
        areaServed: path.includes('/repair-in/') ? { "@type": "Country", name: "United States" } : { "@type": "City", name: "Chicago" },
        serviceType: `${repairType} Repair`,
        description: `Professional ${repairType} repair. Same-day service, 1-month warranty on all parts and labor.`,
        offers: { "@type": "AggregateOffer", priceCurrency: "USD", priceRange: priceRanges[repairType] || '$29 - $599' }
      };
      if (!serviceScript) {
        serviceScript = document.createElement('script');
        serviceScript.id = 'service-schema';
        serviceScript.type = 'application/ld+json';
        document.head.appendChild(serviceScript);
      }
      serviceScript.textContent = JSON.stringify(serviceData);
    } else if (path.startsWith('/sell') || path.startsWith('/sell-device') || path.startsWith('/sell-my')) {
      const deviceType = path.includes('iphone') ? 'iPhone' : path.includes('samsung') ? 'Samsung' : path.includes('macbook') ? 'MacBook' : path.includes('ipad') ? 'iPad' : path.includes('ps5') || path.includes('playstation') ? 'PlayStation' : path.includes('xbox') ? 'Xbox' : path.includes('switch') || path.includes('nintendo') ? 'Nintendo Switch' : path.includes('watch') ? 'Apple Watch' : 'Device';
      const productData = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `Sell Your ${deviceType}`,
        provider: BUSINESS_REF,
        areaServed: { "@type": "Country", "name": "United States" },
        serviceType: "Device Buyback",
        description: `Sell your ${deviceType} for top dollar at 2A Electronics. Instant quotes, FREE shipping, same-day payment. We buy in good working condition nationwide.`,
        offers: { "@type": "AggregateOffer", priceCurrency: "USD", lowPrice: "25", highPrice: "1400" }
      };
      if (!serviceScript) {
        serviceScript = document.createElement('script');
        serviceScript.id = 'service-schema';
        serviceScript.type = 'application/ld+json';
        document.head.appendChild(serviceScript);
      }
      serviceScript.textContent = JSON.stringify(productData);
    } else if (serviceScript) {
      serviceScript.remove();
    }
    
    // Track page view for analytics
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: seo.title,
        page_location: window.location.href,
        page_path: path
      });
    }
    
  }, [location.pathname, overrides]);

  // Enforce SEO Agent overrides even when a page sets its own <Helmet> title (Helmet
  // applies async and would otherwise win). Scoped to paths that actually have an override.
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    const ov = overrides && overrides[path];
    if (!ov || (!ov.title && !ov.description)) return undefined;
    const enforce = () => {
      if (ov.title && document.title !== ov.title) document.title = ov.title;
      if (ov.description) {
        const m = document.querySelector('meta[name="description"]');
        if (m && m.getAttribute('content') !== ov.description) m.setAttribute('content', ov.description);
      }
    };
    enforce();
    const observer = new MutationObserver(enforce);
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });
    const t = setTimeout(enforce, 400);
    return () => { observer.disconnect(); clearTimeout(t); };
  }, [location.pathname, overrides]);
  
  return null; // This component doesn't render anything
};

export default SEOHead;
